import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Table, Badge, Button, Input, Label, Spinner, Container } from 'reactstrap';
import { H5, H6 } from '../../../AbstractElements';
import { ArrowLeft, Printer, Search, Truck, User, FileText, Tool, Calendar, DollarSign, Shield, MapPin, Download, TrendingDown, Users, Phone, Mail, CheckCircle, AlertTriangle } from 'react-feather';
import { useReactToPrint } from 'react-to-print';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { apiPost } from '../../../Utils';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../Context/AuthContext';

const BranchWiseReport = () => {
    // View State
    const [view, setView] = useState<'list' | 'detail'>('list');
    const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);

    // List State
    const [branchList, setBranchList] = useState<any[]>([]);
    const [listLoading, setListLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    // Detail State
    const [reportData, setReportData] = useState<any>(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const { hasPermission } = useAuth();
    const componentRef = useRef(null);

    // --- 1. Fetch Branch List ---
    const fetchBranchList = () => {
        setListLoading(true);
        const payload = { search: searchTerm, page: page };
        apiPost("reports/branch-list", payload).then((res: any) => {
            if (res && res.data) {
                setBranchList(res.data);
                setLastPage(res.last_page);
            }
            setListLoading(false);
        }).catch((err) => {
            console.error(err);
            setListLoading(false);
        });
    };

    useEffect(() => {
        fetchBranchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchTerm]);

    // --- 2. Fetch Detailed Report ---
    const fetchReport = () => {
        if (!selectedBranchId) return;
        setReportLoading(true);
        const payload: any = { id: selectedBranchId };
        if (fromDate) payload.from_date = fromDate.toISOString().split('T')[0];
        if (toDate) payload.to_date = toDate.toISOString().split('T')[0];

        apiPost("reports/branch-overview", payload).then((res: any) => {
            setReportData(res);
            setReportLoading(false);
        }).catch((err) => {
            console.error(err);
            setReportLoading(false);
        });
    };

    useEffect(() => {
        if (view === 'detail') {
            fetchReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBranchId, fromDate, toDate]);

    // --- Handler: Select Branch ---
    const handleSelectBranch = (id: string) => {
        setSelectedBranchId(id);
        setView('detail');
        // Reset filters for new branch
        setFromDate(null);
        setToDate(null);
    };

    // --- Handler: Back to List ---
    const handleBack = () => {
        setView('list');
        setSelectedBranchId(null);
        setReportData(null);
    };

    // --- Export Functions ---
    const handleDownload = () => {
        if (!reportData) return;
        const doc = new jsPDF() as any;

        // Title and Header
        doc.setFontSize(22);
        doc.setTextColor(40, 40, 40);
        doc.text("Branch Overview Report", 14, 20);

        doc.setFontSize(14);
        doc.setTextColor(100);
        doc.text(reportData.branchInfo.name, 14, 28);

        doc.setFontSize(10);
        doc.setTextColor(150);
        const dateStr = `Generated: ${new Date().toLocaleDateString('en-GB')}`;
        const filterStr = (fromDate && toDate)
            ? ` | From: ${fromDate.toLocaleDateString('en-GB')} To: ${toDate.toLocaleDateString('en-GB')}`
            : '';
        doc.text(dateStr + filterStr, 14, 34);

        let currentY = 45;

        // Stats Summary
        const stats = [
            ["Total Vehicles", reportData.stats.total_vehicles],
            ["Active Vehicles", reportData.stats.active_vehicles],
            ["Total Drivers", reportData.stats.total_drivers],
            ["Total Expenses", Number(reportData.stats.total_expense).toLocaleString(undefined, { minimumFractionDigits: 2 })]
        ];

        autoTable(doc, {
            startY: currentY,
            head: [['Metric', 'Value']],
            body: stats,
            theme: 'striped',
            headStyles: { fillColor: [41, 128, 185] }
        });
        currentY = (doc as any).lastAutoTable.finalY + 15;

        // Attention Required (Renewals)
        if (reportData.renewals && reportData.renewals.length > 0) {
            doc.setFontSize(14);
            doc.setTextColor(0);
            doc.text("Attention Required (Overdue/Due Soon)", 14, currentY);
            currentY += 6;

            const renewalRows = reportData.renewals.map((r: any) => [
                r.vehicle, // Vehicle No
                r.type,    // Type
                r.date ? new Date(r.date).toLocaleDateString('en-GB') : '',    // Due Date
                r.status   // Status
            ]);

            autoTable(doc, {
                startY: currentY,
                head: [['Vehicle', 'Type', 'Due Date', 'Status']],
                body: renewalRows,
                theme: 'grid',
                headStyles: { fillColor: [220, 53, 69] } // Red header for alerts
            });
            currentY = (doc as any).lastAutoTable.finalY + 15;
        }

        // Branch Vehicles List
        if (reportData.vehicles && reportData.vehicles.length > 0) {
            doc.setFontSize(14);
            doc.setTextColor(0);
            doc.text("Branch Vehicles", 14, currentY);
            currentY += 6;

            const vehicleRows = reportData.vehicles.map((v: any) => [
                v.reg_no,
                v.make_model,
                v.driver,
                v.status
            ]);

            autoTable(doc, {
                startY: currentY,
                head: [['Registration No', 'Make & Model', 'Driver', 'Status']],
                body: vehicleRows,
                theme: 'grid',
                headStyles: { fillColor: [25, 135, 84] } // Green header
            });
            currentY = (doc as any).lastAutoTable.finalY + 15;
        }

        // Expense History
        doc.setFontSize(14);
        doc.setTextColor(0);
        doc.text("Expense History", 14, currentY);
        currentY += 6;

        const expenses = reportData.expenses.map((e: any) => [
            e.date ? new Date(e.date).toLocaleDateString('en-GB') : '-',
            e.vehicle_reg ? `${e.vehicle_reg} - ${e.type}` : e.type, // Combined for cleaner print
            Number(e.amount).toFixed(2)
        ]);

        autoTable(doc, {
            startY: currentY,
            head: [['Date', 'Description', 'Amount']],
            body: expenses.length ? expenses : [['No expenses found', '', '']],
            theme: 'grid',
            headStyles: { fillColor: [100, 100, 100] }
        });

        doc.save(`BranchReport_${reportData.branchInfo.name}.pdf`);
    };

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });


    // --- Render: List View ---
    const renderListView = () => (
        <>
            <Card className="mb-4 border-0 shadow-none" style={{ background: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(10px)', borderRadius: '15px' }}>
                <CardBody>
                    <Row className="g-3 justify-content-end">
                        <Col md={3}>
                            <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1">
                                <Search size={14} /> Quick Search
                            </Label>
                            <Input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                bsSize="sm"
                                className="form-control-light border-0 bg-white shadow-sm"
                                style={{ borderRadius: '8px', padding: '8px 12px' }}
                            />
                        </Col>
                    </Row>

                </CardBody>
            </Card>

            {listLoading ? (
                <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}>
                    <Spinner color="primary" />
                </div>
            ) : (
                <Row>
                    {branchList.map((branch: any) => (
                        <Col md={6} xl={3} key={branch.id} className="mb-4">
                            <Card
                                className="h-100 border-0 shadow-sm card-hover overflow-hidden"
                                style={{ cursor: 'pointer', transition: 'all 0.3s ease', borderRadius: '16px' }}
                                onClick={() => handleSelectBranch(branch.id)}
                            >
                                <div className="p-4" style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Badge color={branch.status === 'Active' ? 'success' : 'secondary'} className="px-3 py-1 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            {branch.status || 'Active'}
                                        </Badge>
                                        <div className="bg-white rounded-circle p-2 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                            <MapPin size={20} className="text-primary" />
                                        </div>
                                    </div>
                                    <H5 className="mb-1 text-dark fw-bold lh-sm" style={{ fontSize: '1rem' }}>{branch.branch_name}</H5>
                                    <div className="d-flex align-items-start text-muted small mt-2">
                                        <MapPin size={14} className="me-1 text-secondary flex-shrink-0 mt-1" style={{ minWidth: '14px' }} />
                                        <span className="lh-sm">{branch.address || 'No Address Provided'}</span>
                                    </div>
                                </div>

                                <CardBody className="p-4 pt-1">
                                    <div className="row g-2 mt-2">
                                        <div className="col-6">
                                            <div className="p-3 bg-light rounded-3 text-center h-100 border border-light">
                                                <Truck size={20} className="text-primary mb-2" />
                                                <h5 className="mb-0 fw-bold text-dark">{branch.active_vehicles_count}</h5>
                                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>Active Vehicles</small>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="p-3 bg-light rounded-3 text-center h-100 border border-light d-flex flex-column justify-content-center">
                                                <Users size={20} className="text-info mb-2 mx-auto" />
                                                <h5 className="mb-0 fw-bold text-dark text-break lh-sm" style={{ fontSize: '1rem' }}>{branch.manager_name || 'N/A'}</h5>
                                                <small className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>Manager</small>
                                            </div>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                    {branchList.length === 0 && <Col xs={12}><div className="text-center py-5 text-muted">No branches found</div></Col>}
                </Row>
            )}
            {/* Styled Pagination */}
            {lastPage > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <nav aria-label="Page navigation">
                        <ul className="pagination pagination-primary">
                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                <a className="page-link" href="#javascript" onClick={() => setPage(page - 1)}>Previous</a>
                            </li>
                            {[...Array(lastPage)].map((_, i) => {
                                const pageNum = i + 1;
                                return (
                                    <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                                        <a className="page-link" href="#javascript" onClick={() => setPage(pageNum)}>{pageNum}</a>
                                    </li>
                                );
                            })}
                            <li className={`page-item ${page === lastPage ? 'disabled' : ''}`}>
                                <a className="page-link" href="#javascript" onClick={() => setPage(page + 1)}>Next</a>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}
        </>
    );

    // --- Render: Detail View ---
    const renderDetailView = () => (
        <>
            <div className="d-flex justify-content-between align-items-center  mb-4 border-0 shadow-none p-3" style={{ background: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(10px)', borderRadius: '15px', position: 'relative', zIndex: 5 }}>
                <H5 className="mb-0 fw-bold"></H5>
                <div className="d-flex gap-2 align-items-end flex-wrap justify-content-end">
                    <div className="me-2">
                        <Label className="small text-info fw-bold mb-1 d-flex align-items-center gap-1">
                            <Calendar size={14} /> From Date
                        </Label>
                        <div className="bg-white rounded shadow-sm p-1" style={{ minWidth: '130px' }}>
                            <DatePicker
                                selected={fromDate}
                                onChange={(date: Date) => setFromDate(date)}
                                placeholderText="Start Date"
                                className="form-control form-control-sm border-0 bg-transparent ps-2"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                            />
                        </div>
                    </div>
                    <div className="me-2">
                        <Label className="small text-info fw-bold mb-1 d-flex align-items-center gap-1">
                            <Calendar size={14} /> To Date
                        </Label>
                        <div className="bg-white rounded shadow-sm p-1" style={{ minWidth: '130px' }}>
                            <DatePicker
                                selected={toDate}
                                onChange={(date: Date) => setToDate(date)}
                                placeholderText="End Date"
                                className="form-control form-control-sm border-0 bg-transparent ps-2"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                            />
                        </div>
                    </div>

                    <Button
                        color="info"
                        className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm text-white"
                        onClick={handleBack}
                    >
                        <ArrowLeft size={16} /> <span className="fw-bold d-none d-sm-inline">Back</span>
                    </Button>

                    {hasPermission('branch-report.download') && (
                        <Button
                            color="success"
                            className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm"
                            onClick={handleDownload}
                            disabled={!reportData}
                        >
                            <Download size={16} /> <span className="fw-bold d-none d-sm-inline">Download</span>
                        </Button>
                    )}
                    {hasPermission('branch-report.print') && (
                        <Button
                            color="primary"
                            className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm"
                            onClick={handlePrint}
                            disabled={!reportData}
                        >
                            <Printer size={16} /> <span className="fw-bold d-none d-sm-inline">Print</span>
                        </Button>
                    )}
                </div>
            </div>

            {reportLoading || !reportData ? (
                <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}>
                    <Spinner color="primary" />
                </div>
            ) : (
                <div ref={componentRef} className="bg-transparent">
                    {/* Header Info */}
                    <Card className="mb-4 border-0 shadow-sm overflow-hidden">
                        <div className="bg-primary p-4 text-white d-flex justify-content-between align-items-start">
                            <div>
                                <h2 className="mb-1 fw-bold">{reportData.branchInfo.name} Report</h2>
                                <p className="mb-0 opacity-75"><MapPin size={14} className="me-1" /> {reportData.branchInfo.address || 'Address not available'}</p>
                            </div>
                            <div className="text-end">
                                <h5 className="mb-1">{reportData.branchInfo.manager || 'No Manager'}</h5>
                                <p className="mb-0 opacity-75">{reportData.branchInfo.contact}</p>
                            </div>
                        </div>
                    </Card>

                    {/* KPI Cards */}
                    <Row className="mb-4 g-3">
                        <Col md={3}>
                            <Card className="h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)', borderLeft: '4px solid #dc3545' }}>
                                <CardBody className="d-flex align-items-center justify-content-between p-4">
                                    <div>
                                        <h6 className="text-danger text-uppercase small fw-bold mb-2">Total Expenses</h6>
                                        <h3 className="mb-0 fw-bold text-dark">{Number(reportData.stats.total_expense).toLocaleString(undefined, { minimumFractionDigits: 2 })}</h3>
                                    </div>
                                    <div className="p-3 bg-white rounded-circle shadow-sm">
                                        <DollarSign className="text-danger" size={24} />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)', borderLeft: '4px solid #0d6efd' }}>
                                <CardBody className="d-flex align-items-center justify-content-between p-4">
                                    <div>
                                        <h6 className="text-primary text-uppercase small fw-bold mb-2">Total Vehicles</h6>
                                        <h3 className="mb-0 fw-bold text-dark">{reportData.stats.total_vehicles}</h3>
                                    </div>
                                    <div className="p-3 bg-white rounded-circle shadow-sm">
                                        <Truck className="text-primary" size={24} />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #e6fffa 0%, #ffffff 100%)', borderLeft: '4px solid #198754' }}>
                                <CardBody className="d-flex align-items-center justify-content-between p-4">
                                    <div>
                                        <h6 className="text-success text-uppercase small fw-bold mb-2">Active Vehicles</h6>
                                        <h3 className="mb-0 fw-bold text-dark">{reportData.stats.active_vehicles}</h3>
                                    </div>
                                    <div className="p-3 bg-white rounded-circle shadow-sm">
                                        <CheckCircle className="text-success" size={24} />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="h-100 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #ffffff 100%)', borderLeft: '4px solid #0dcaf0' }}>
                                <CardBody className="d-flex align-items-center justify-content-between p-4">
                                    <div>
                                        <h6 className="text-info text-uppercase small fw-bold mb-2">Drivers</h6>
                                        <h3 className="mb-0 fw-bold text-dark">{reportData.stats.total_drivers}</h3>
                                    </div>
                                    <div className="p-3 bg-white rounded-circle shadow-sm">
                                        <Users className="text-info" size={24} />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Content Section */}
                    <Row>
                        {/* LEFT COLUMN: Financial History */}
                        <Col lg={7} className="mb-4">
                            <Card className="h-100 border-0 shadow-sm">
                                <CardHeader className="bg-white border-bottom py-3">
                                    <H6 className="mb-0">Financial History</H6>
                                </CardHeader>
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0">
                                        <thead className="table-light small">
                                            <tr>
                                                <th>Date</th>
                                                <th>Vehicle</th>
                                                <th>Type</th>
                                                <th className="text-end">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.expenses.map((e: any, i: number) => (
                                                <tr key={i}>
                                                    <td>{e.date ? new Date(e.date).toLocaleDateString('en-GB') : '-'}</td>
                                                    <td>
                                                        <span className="d-block text-dark fw-bold">{e.vehicle_reg}</span>
                                                        <small className="text-muted text-truncate d-block" style={{ maxWidth: '250px' }}>{e.description}</small>
                                                    </td>
                                                    <td>
                                                        <Badge color={
                                                            e.type === 'Fuel Expense' ? 'danger' :
                                                                (e.type === 'Insurance' ? 'success' :
                                                                    (e.type === 'RC Renewal' ? 'info' :
                                                                        (e.type === 'Maintenance' ? 'warning' :
                                                                            (e.type === 'LPO' ? 'primary' :
                                                                                (e.type === 'Accident' ? 'dark' : 'secondary')))))
                                                        } className="px-2">{e.type}</Badge>
                                                    </td>
                                                    <td className="text-end fw-bold text-dark">{Number(e.amount).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                            {reportData.expenses.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-muted">No records found</td></tr>}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card>
                        </Col>

                        {/* RIGHT COLUMN: Attention & Vehicles */}
                        <Col lg={5} className="mb-4">
                            {/* 1. Attention Required (Renewals) */}
                            {reportData.renewals && reportData.renewals.length > 0 && (
                                <Card className="border-0 shadow-sm border-start border-warning border-4 mb-4">
                                    <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="p-2 bg-light-warning rounded-circle">
                                                <AlertTriangle className="text-warning" size={18} />
                                            </div>
                                            <H6 className="mb-0">Attention Required</H6>
                                        </div>
                                    </CardHeader>
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0">
                                            <thead className="table-light small">
                                                <tr>
                                                    <th>Vehicle</th>
                                                    <th>Type</th>
                                                    <th className="text-end">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.renewals.map((r: any, i: number) => (
                                                    <tr key={i}>
                                                        <td className="fw-bold text-dark">{r.vehicle}</td>
                                                        <td>
                                                            <span className="d-block text-dark fw-medium small">{r.type}</span>
                                                            <span className="text-muted small">{r.date ? new Date(r.date).toLocaleDateString('en-GB') : ''}</span>
                                                        </td>
                                                        <td className="text-end">
                                                            <Badge color={r.status_color} className="px-2">
                                                                {r.status}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card>
                            )}

                            {/* 2. Branch Vehicles */}
                            <Card className="border-0 shadow-sm">
                                <CardHeader className="bg-white border-bottom py-3">
                                    <H6 className="mb-0">Branch Vehicles</H6>
                                </CardHeader>
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <Table size="sm" hover className="mb-0 align-middle">
                                        <tbody>
                                            {reportData.vehicles.map((v: any) => (
                                                <tr key={v.id}>
                                                    <td className="p-3">
                                                        <div className="d-flex align-items-center">
                                                            <div className={`p-2 rounded-circle me-3 ${v.status === 'Active' ? 'bg-light-success text-success' : 'bg-light-warning text-warning'}`}>
                                                                <Truck size={16} />
                                                            </div>
                                                            <div>
                                                                <span className="d-block fw-bold text-dark">{v.reg_no}</span>
                                                                <small className="text-muted">{v.make_model}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-end p-3">
                                                        <Badge color={v.status === 'Active' ? 'light-success' : 'light-warning'} pill className={`text-${v.status === 'Active' ? 'success' : 'warning'}`}>
                                                            {v.status}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                            {reportData.vehicles.length === 0 && <tr><td colSpan={2} className="text-center py-4 text-muted">No vehicles</td></tr>}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            )}
        </>
    );

    return (
        <div className="page-body">
            <Breadcrumbs
                pageTitle={view === 'detail' ? "Branch Details" : "Branch Selection"}
                parent="Reports"
                title={view === 'detail' ? "Branch Report" : "Branch Wise"}
            />
            <Container fluid>
                {view === 'list' ? renderListView() : renderDetailView()}
            </Container>
        </div>
    );
};

export default BranchWiseReport;
